'use client';

import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';

const AIScoreChart = dynamic(() => import('./AIScoreChart'), { ssr: false });

const insights = [
  {
    id: 'ai-fair-price',
    title: 'AI Fair Price Indicator',
    value: 'At Market Value',
    detail: 'Based on 847 comparable sales in Saadiyat Island over the last 12 months, this property is priced within 2.3% of the median market value.',
    score: 78,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    icon: 'ScaleIcon',
  },
  {
    id: 'ai-investment',
    title: 'AI Investment Score',
    value: '8.4 / 10',
    detail: 'Strong investment fundamentals — high demand zone, freehold ownership, excellent rental demand, and consistent capital appreciation of 9.2% YoY.',
    score: 84,
    color: 'text-primary',
    bg: 'bg-primary/8',
    icon: 'ChartBarIcon',
  },
  {
    id: 'ai-yield',
    title: 'AI Rental Yield',
    value: '4.2% Gross',
    detail: 'Estimated annual rental income: AED 777,000 based on comparable leases in Saadiyat Beach Villas. Net yield after service charges: ~3.6%.',
    score: 42,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    icon: 'BanknotesIcon',
  },
  {
    id: 'ai-appreciation',
    title: 'AI Appreciation Forecast',
    value: '+9.2% / yr',
    detail: 'Saadiyat Island has shown consistent appreciation driven by ADGM expansion, cultural district development, and limited villa supply.',
    score: 92,
    color: 'text-primary',
    bg: 'bg-primary/8',
    icon: 'ArrowTrendingUpIcon',
  },
  {
    id: 'ai-area',
    title: 'AI Area Intelligence',
    value: 'Grade A+',
    detail: 'Walkability: 72/100 | Lifestyle Score: 94/100 | Schools: 8 within 5km | Hospitals: 3 premium | Beach Access: Direct',
    score: 94,
    color: 'text-primary',
    bg: 'bg-primary/8',
    icon: 'MapIcon',
  },
  {
    id: 'ai-fraud',
    title: 'AI Fraud Detection',
    value: 'No Risk Detected',
    detail: 'This listing passed all 47 fraud detection checks. Title deed verified, agent licensed, price consistent with market data, no duplicate listing signals.',
    score: 100,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    icon: 'ShieldCheckIcon',
  },
];

export default function AIInsights() {
  const [expanded, setExpanded] = useState<string | null>('ai-investment');

  return (
    <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
          <Icon name="SparklesIcon" size={18} className="text-primary" />
        </div>
        <div>
          <h2 className="font-bold text-foreground text-lg">OROW AI Insights</h2>
          <p className="text-xs text-muted-foreground">Powered by OROW Intelligence Engine — updated 07 Jun 2026</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {insights.map((ins) => (
          <div
            key={ins.id}
            className={`rounded-xl border transition-all duration-200 cursor-pointer ${
              expanded === ins.id ? 'border-primary/30 shadow-card-hover' : 'border-border hover:border-primary/20'
            }`}
            onClick={() => setExpanded(expanded === ins.id ? null : ins.id)}
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg ${ins.bg} flex items-center justify-center`}>
                    <Icon name={ins.icon as 'ScaleIcon'} size={16} className={ins.color} />
                  </div>
                  <span className="text-sm font-semibold text-foreground">{ins.title}</span>
                </div>
                <Icon name={expanded === ins.id ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={14} className="text-muted-foreground" />
              </div>
              <div className={`text-lg font-extrabold ${ins.color} font-tabular`}>{ins.value}</div>

              {/* Score bar */}
              <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${ins.score >= 80 ? 'bg-primary' : ins.score >= 60 ? 'bg-amber-500' : 'bg-red-400'}`}
                  style={{ width: `${ins.score}%` }}
                />
              </div>
            </div>

            {expanded === ins.id && (
              <div className="px-4 pb-4 border-t border-border/50 pt-3">
                <p className="text-xs text-muted-foreground leading-relaxed">{ins.detail}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* AI Score Radial */}
      <div className="mt-5 p-4 bg-muted/40 rounded-xl">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-foreground">Overall OROW Score</span>
          <span className="text-2xl font-extrabold text-primary font-tabular">8.4 / 10</span>
        </div>
        <AIScoreChart />
      </div>
    </div>
  );
}